import {
  AfterContentInit,
  Component,
  EventEmitter,
  Inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Optional,
  Output,
  SimpleChanges,
} from '@angular/core';
import { CbreLogger, CbreLoggerToken } from '@cbre/logger';
import { Column } from '@miq/catalog';
import { XColumn } from '@records/catalog/extension.model';
// tslint:disable: max-line-length
import { SavableValidatableDecorator } from '@records/records-search/records-details/savable-validatable.component';
import {
  MiqBehaviors,
  MiqRegisteredBehaviorType,
  MiqTouchable,
  MiqValidatable,
  validateElements,
} from '@shared/behaviors';
import { Broadcaster } from '@shared/behaviors/broadcaster';
import { CustomInputConfig, TextInputConfig } from '@shared/models/icommons.model';
import { LookupRefService } from '@shared/services/lookup-ref.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { asapScheduler, asyncScheduler, Observable } from 'rxjs';
import { observeOn, take, tap } from 'rxjs/operators';

/**
 * @deprecated use miq-column-renderer, miq-input or miq-input-control
 */
@UntilDestroy()
@Component({
  selector: 'miq-record-input',
  templateUrl: './record-input.component.html',
  styleUrls: ['./record-input.component.scss'],
  providers: [{ provide: MiqBehaviors, useExisting: RecordInputComponent }],
  standalone: false,
})
export class RecordInputComponent
  extends SavableValidatableDecorator
  implements OnInit, OnChanges, AfterContentInit, OnDestroy, MiqValidatable, MiqTouchable
{
  @Input() recordDetail: XColumn;
  @Input() isNewRecord: boolean;
  @Input() isDisabled: boolean;
  @Input() record: any;
  @Input() defaultEmptyValue: any;
  @Input() isCloneRecord: boolean;
  @Input() userConfidentialityLevel: string;
  @Input() recordConfidentialityLevel: string;
  @Input() isRecordDetail = false;
  @Input() customConfigs: CustomInputConfig = {};

  @Output() rdChange = new EventEmitter<any>();
  @Output() clear = new EventEmitter<string>();

  get customTextConfigs(): TextInputConfig {
    return this.customConfigs.text;
  }

  get isDisabledInput(): boolean {
    return (
      (!this.isNewRecord &&
        (!this.recordDetail.parent[this.recordDetail.edpUpdateMethod || 'update'] || this.isDisabled)) ||
      (this.isNewRecord && this.isDisabled)
    );
  }

  private initializerStream$: Observable<any>;
  private changeEventStream$: Observable<any>;

  constructor(
    private lookupRefService: LookupRefService,
    @Optional() private broadcaster: Broadcaster,
    @Inject(CbreLoggerToken) private logger: CbreLogger
  ) {
    super();
  }

  private get isPublisherInput() {
    return this.recordDetail.publisher && this.broadcaster;
  }

  private get isSubscriberInput() {
    return !!this.recordDetail.subscribeOn && !!this.recordDetail.subscribeOn.targetColumnId;
  }

  private get isConfidentialityInput() {
    return this.recordDetail && this.recordDetail.field === 'confidentiality_f' && this.recordDetail.values;
  }

  private get touchableElements() {
    return this.elementsToValidate.toArray().ofType(MiqRegisteredBehaviorType.Touchable);
  }

  ngOnChanges({ record }: SimpleChanges): void {
    if (record && !record.firstChange) {
      this.makeInitializerStreams();
      this.publishInitValues();
    }
  }

  ngOnInit() {
    this.makeBroadcastSubscription();
    this.initConfidentialityLevelAllowances();
  }

  ngAfterContentInit(): void {
    this.publishInitValues();
  }

  ngOnDestroy() {
    // required by untilDestroyed
    this.elementsToValidate = null;
    if (this.initializerStream$) {
      this.initializerStream$ = null;
    }
    if (this.changeEventStream$) {
      this.changeEventStream$ = null;
    }
  }

  private publishInitValues() {
    if (this.isPublisherInput) {
      const action = this.recordDetail.publisherAction || this.recordDetail.columnId;
      this.broadcaster.broadcast({
        firstChange: true,
        change: this.recordDetail,
        action,
      });
    }
  }

  private makeBroadcastSubscription() {
    if (this.isSubscriberInput && this.broadcaster) {
      this.makeInitializerStreams();
      this.makeChangeStreams();
    }
  }

  private makeInitializerStreams() {
    if (this.isSubscriberInput) {
      const { targetColumnId } = this.recordDetail.subscribeOn;
      const initOpts = { emit: false, updateValue: false };

      this.broadcaster
        .getTargetedInitializerStream(targetColumnId)
        .pipe(
          take(1),
          tap(change => this.fetchNextLOVList(change)),
          // need to apply in async way since initialization runs might fall in Expression Check Error
          tap(change => asapScheduler.schedule(this.applyChanges.bind(this, change, initOpts)))
        )
        .subscribe();
    }
  }

  private makeChangeStreams() {
    const { targetColumnId, fetchOnChange = true } = this.recordDetail.subscribeOn;
    const updateOpts = { emit: true, updateValue: true };

    this.broadcaster
      .getTargetedChangesStream(targetColumnId)
      .pipe(
        untilDestroyed(this),
        observeOn(asyncScheduler),
        tap(({ change }) => (fetchOnChange ? this.fetchNextLOVList(change) : null)),
        tap(({ change }) => this.applyChanges(change, updateOpts)),
        tap(() => this.proxyEvent())
      )
      .subscribe();
  }

  private applyChanges(change: Column, options: { emit: boolean; updateValue: boolean }) {
    const { columnBehaviorModifier, valueSetter = () => null } = this.recordDetail.subscribeOn;

    const valueChange = options.updateValue ? { value: valueSetter(change, this.recordDetail) } : {};
    const columnChange = columnBehaviorModifier ? columnBehaviorModifier(change) : {};
    this.recordDetail = Object.assign(this.recordDetail, valueChange, columnChange);

    if (options.emit) {
      this.rdChange.emit(this.recordDetail);
    }
  }

  private fetchNextLOVList({ value }: Column) {
    if (this.recordDetail.lookupCategory) {
      this.lookupRefService
        .loadDataFor(this.recordDetail.lookupCategory, false, true, [value])
        .subscribe(values => (this.recordDetail.values = values));
    }
  }

  private proxyEvent() {
    const { proxy } = this.recordDetail.subscribeOn;
    return proxy
      ? this.broadcaster.broadcast({ change: this.recordDetail, action: this.recordDetail.publisherAction })
      : null;
  }

  private initConfidentialityLevelAllowances() {
    if (this.isConfidentialityInput) {
      this.recordDetail.values.forEach(
        confidentiality => (confidentiality.disabled = this.isInsufficientConfidentiality(confidentiality))
      );
    }
  }

  private isInsufficientConfidentiality(confidentiality: { code: string }) {
    return (
      this.userConfidentialityLevel < this.recordConfidentialityLevel &&
      confidentiality.code < this.recordConfidentialityLevel
    );
  }

  broadcastChange() {
    this.rdChange.emit(this.recordDetail);
    if (!this.isPublisherInput) {
      this.logger.warn('Provide BroadCaster in your Reactive Component for isPublisherInput to be not null');
    }
    return this.isPublisherInput
      ? this.broadcaster.broadcast({ change: this.recordDetail, action: this.recordDetail.publisherAction })
      : null;
  }

  isTouched() {
    return this.touchableElements.some(element => element.isTouched());
  }

  markAsTouched() {
    this.touchableElements.forEach(element => element.markAsTouched());
  }

  isValid(): boolean {
    return validateElements(this.validatableElements);
  }
}
